import { useState, useEffect } from "react";
import { 
  CheckCircle, Euro, Calculator, ChevronRight, AlertTriangle,
  Info, Home, Thermometer, Grid, Wind, Droplet, Flame, Sun, Layers,
  Plus, Trash2, Save, Share2, Download, HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icônes par type de produit
const PRODUCT_ICONS = {
  isolation_combles: Home,
  isolation_murs: Layers,
  isolation_planchers: Grid,
  pompe_chaleur: Thermometer,
  pac_air_eau: Thermometer,
  pac_geothermie: Thermometer,
  chaudiere_biomasse: Flame,
  poele_bois: Flame,
  poele_granules: Flame,
  fenetres: Grid,
  ventilation: Wind,
  vmc_double_flux: Wind,
  chauffe_eau_thermodynamique: Droplet,
  chauffe_eau_solaire: Sun,
  default: Calculator
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "N/A";
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    maximumFractionDigits: 0 
  }).format(amount);
};

/**
 * Composant pour afficher les questions dynamiques d'un produit
 */
function ProductQuestionForm({ workType, questions, answers, onAnswerChange, prices }) {
  const Icon = PRODUCT_ICONS[workType] || PRODUCT_ICONS.default;
  
  // Filtrer les questions visibles (conditions)
  const visibleQuestions = questions.filter(q => {
    if (!q.condition) return true;
    // Évaluer la condition simple
    try {
      const condition = q.condition.replace(/(\w+)/g, (match) => {
        if (match in answers) return JSON.stringify(answers[match]);
        return match;
      });
      return eval(condition);
    } catch {
      return true;
    }
  });

  return (
    <div className="space-y-4">
      {visibleQuestions.map((question) => (
        <div key={question.id} className="space-y-2">
          <Label htmlFor={question.id} className="flex items-center gap-2">
            {question.label}
            {question.required && <span className="text-red-500">*</span>}
            {question.help && (
              <span className="text-slate-400 cursor-help" title={question.help}>
                <HelpCircle className="h-4 w-4" />
              </span>
            )}
          </Label>
          
          {question.type === "number" && (
            <div className="flex items-center gap-2">
              <Input
                id={question.id}
                type="number"
                min={question.min}
                max={question.max}
                value={answers[question.id] || ""}
                onChange={(e) => onAnswerChange(question.id, e.target.value)}
                placeholder={question.placeholder}
                className="h-11"
              />
              {question.unit && (
                <span className="text-sm text-slate-500 w-12">{question.unit}</span>
              )}
            </div>
          )}
          
          {question.type === "select" && (
            <Select
              value={answers[question.id] || question.default || ""}
              onValueChange={(value) => onAnswerChange(question.id, value)}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {question.options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {question.help && question.type !== "select" && (
            <p className="text-xs text-slate-500">{question.help}</p>
          )}
        </div>
      ))}
      
      {/* Afficher la fourchette de prix selon la gamme */}
      {prices && answers.gamme && (
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600">
            <strong>Gamme sélectionnée:</strong> {prices[answers.gamme]?.label}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {prices[answers.gamme]?.description}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Composant principal du mini-audit détaillé
 */
export default function MiniAuditSection({ 
  leadId, 
  workTypes = [], 
  incomeCategory,
  department,
  housingSurface,
  onAuditComplete
}) {
  const [activeTab, setActiveTab] = useState("eligibility");
  const [questions, setQuestions] = useState({});
  const [prices, setPrices] = useState({});
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [additionalWorks, setAdditionalWorks] = useState([]);
  const [showAddWorkModal, setShowAddWorkModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);

  // Charger les questions et prix au montage
  useEffect(() => {
    const loadData = async () => {
      try {
        // Charger les prix du marché
        const pricesRes = await axios.get(`${API}/market-prices`);
        setPrices(pricesRes.data.products || {});
        setAvailableProducts(Object.entries(pricesRes.data.products || {}).map(([id, p]) => ({
          id,
          label: p.label,
          category: p.category
        })));

        // Charger les questions pour chaque type de travaux
        const questionsData = {};
        for (const workType of workTypes) {
          try {
            const qRes = await axios.get(`${API}/mini-audit/questions/${workType}`);
            questionsData[workType] = qRes.data;
          } catch (e) {
            console.warn(`Questions non trouvées pour ${workType}`);
          }
        }
        setQuestions(questionsData);

        // Initialiser les réponses avec les valeurs par défaut
        const initialAnswers = {};
        workTypes.forEach(wt => {
          initialAnswers[wt] = {
            gamme: "medium"
          };
          // Pré-remplir la surface si disponible
          if (housingSurface) {
            initialAnswers[wt].surface_combles = housingSurface;
            initialAnswers[wt].surface_murs = Math.round(housingSurface * 2.5);
            initialAnswers[wt].surface_plancher = housingSurface;
            initialAnswers[wt].surface_chauffee = housingSurface;
            initialAnswers[wt].surface_logement = housingSurface;
          }
        });
        setAnswers(initialAnswers);

      } catch (error) {
        console.error("Erreur chargement données mini-audit:", error);
        toast.error("Erreur lors du chargement des données");
      }
    };

    if (workTypes.length > 0) {
      loadData();
    }
  }, [workTypes, housingSurface]);

  // Mettre à jour une réponse
  const handleAnswerChange = (workType, questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [workType]: {
        ...prev[workType],
        [questionId]: value
      }
    }));
    // Effacer le résultat précédent pour ce travail
    setResults(prev => {
      const newResults = { ...prev };
      delete newResults[workType];
      return newResults;
    });
  };

  // Calculer l'estimation pour un type de travaux
  const calculateForWork = async (workType) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mini-audit/calculate`, {
        lead_id: leadId,
        work_type: workType,
        answers: answers[workType] || {},
        gamme: answers[workType]?.gamme || "medium"
      });
      
      setResults(prev => ({
        ...prev,
        [workType]: response.data
      }));
      
      toast.success(`Estimation calculée pour ${prices[workType]?.label || workType}`);
    } catch (error) {
      console.error("Erreur calcul:", error);
      toast.error("Erreur lors du calcul");
    } finally {
      setLoading(false);
    }
  };

  // Calculer tous les travaux
  const calculateAll = async () => {
    setLoading(true);
    const allWorkTypes = [...workTypes, ...additionalWorks];
    
    for (const workType of allWorkTypes) {
      try {
        const response = await axios.post(`${API}/mini-audit/calculate`, {
          lead_id: leadId,
          work_type: workType,
          answers: answers[workType] || {},
          gamme: answers[workType]?.gamme || "medium"
        });
        
        setResults(prev => ({
          ...prev,
          [workType]: response.data
        }));
      } catch (error) {
        console.error(`Erreur calcul ${workType}:`, error);
      }
    }
    
    setLoading(false);
    toast.success("Toutes les estimations ont été calculées");
  };

  // Sauvegarder le mini-audit
  const saveAudit = async () => {
    setSaving(true);
    try {
      const totalCost = Object.values(results).reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
      const totalAids = Object.values(results).reduce((sum, r) => 
        sum + Object.values(r.aids_estimated || {}).reduce((a, b) => a + b, 0), 0
      );

      await axios.post(`${API}/mini-audit/save`, {
        lead_id: leadId,
        work_audits: {
          answers,
          results
        },
        total_estimated_cost: totalCost,
        total_aids: totalAids,
        reste_a_charge: Math.max(0, totalCost - totalAids)
      });

      toast.success("Mini-audit sauvegardé !");
      
      if (onAuditComplete) {
        onAuditComplete({
          totalCost,
          totalAids,
          resteACharge: Math.max(0, totalCost - totalAids)
        });
      }
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Ajouter un travail supplémentaire
  const addWork = async (workType) => {
    if (additionalWorks.includes(workType) || workTypes.includes(workType)) {
      toast.error("Ce type de travaux est déjà sélectionné");
      return;
    }

    setAdditionalWorks(prev => [...prev, workType]);
    
    // Charger les questions pour ce nouveau travail
    try {
      const qRes = await axios.get(`${API}/mini-audit/questions/${workType}`);
      setQuestions(prev => ({
        ...prev,
        [workType]: qRes.data
      }));
      setAnswers(prev => ({
        ...prev,
        [workType]: { gamme: "medium", surface_chauffee: housingSurface }
      }));
    } catch (e) {
      console.warn(`Questions non trouvées pour ${workType}`);
    }

    setShowAddWorkModal(false);
    toast.success(`${prices[workType]?.label || workType} ajouté`);
  };

  // Supprimer un travail supplémentaire
  const removeWork = (workType) => {
    setAdditionalWorks(prev => prev.filter(w => w !== workType));
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[workType];
      return newAnswers;
    });
    setResults(prev => {
      const newResults = { ...prev };
      delete newResults[workType];
      return newResults;
    });
    toast.info("Travaux retirés");
  };

  // Calculer les totaux
  const totals = {
    cost: Object.values(results).reduce((sum, r) => sum + (r.estimated_cost || 0), 0),
    aids: Object.values(results).reduce((sum, r) => 
      sum + Object.values(r.aids_estimated || {}).reduce((a, b) => a + b, 0), 0
    ),
    get resteACharge() { return Math.max(0, this.cost - this.aids); },
    get tauxFinancement() { return this.cost > 0 ? Math.round((this.aids / this.cost) * 100) : 0; }
  };

  const allWorkTypes = [...workTypes, ...additionalWorks];

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="eligibility" className="text-sm font-medium">
            <CheckCircle className="h-4 w-4 mr-2" />
            Éligibilité
          </TabsTrigger>
          <TabsTrigger value="detailed" className="text-sm font-medium">
            <Calculator className="h-4 w-4 mr-2" />
            Estimation détaillée
          </TabsTrigger>
        </TabsList>

        {/* ONGLET 1: ÉLIGIBILITÉ SIMPLIFIÉE */}
        <TabsContent value="eligibility" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1A4D2E]">Votre éligibilité aux aides</CardTitle>
              <CardDescription>
                Aperçu des aides auxquelles vous pouvez prétendre
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#E8F5E9] border border-[#1A4D2E]/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="h-6 w-6 text-[#1A4D2E]" />
                  <span className="font-bold text-[#1A4D2E]">Vous êtes éligible !</span>
                </div>
                <p className="text-sm text-slate-600">
                  Catégorie de revenus : <strong className="capitalize">{incomeCategory}</strong>
                </p>
              </div>

              {/* Liste des aides avec montants max */}
              <div className="space-y-3">
                {allWorkTypes.map(workType => {
                  const productPrice = prices[workType];
                  const Icon = PRODUCT_ICONS[workType] || PRODUCT_ICONS.default;
                  
                  return (
                    <div 
                      key={workType}
                      className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-[#1A4D2E]" />
                          </div>
                          <div>
                            <h4 className="font-medium">{productPrice?.label || workType}</h4>
                            <p className="text-sm text-slate-500">
                              {productPrice?.prices?.medium?.description || ""}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Jusqu'à</p>
                          <p className="font-bold text-[#1A4D2E]">
                            {productPrice?.prices?.high?.price 
                              ? formatCurrency(productPrice.prices.high.price)
                              : "Variable"
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          MaPrimeRénov' éligible
                        </span>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                          Prime CEE éligible
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CTA pour aller à l'estimation détaillée */}
              <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2d6b47] rounded-xl p-4 text-white">
                <p className="font-medium mb-2">Obtenez une estimation précise</p>
                <p className="text-sm text-white/80 mb-3">
                  Répondez à quelques questions pour calculer le montant exact de vos aides.
                </p>
                <Button 
                  onClick={() => setActiveTab("detailed")}
                  className="bg-white text-[#1A4D2E] hover:bg-white/90"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Lancer le mini-audit
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ONGLET 2: ESTIMATION DÉTAILLÉE (MINI-AUDIT) */}
        <TabsContent value="detailed" className="mt-6 space-y-6">
          {/* En-tête avec totaux */}
          {Object.keys(results).length > 0 && (
            <Card className="bg-gradient-to-br from-[#E8F5E9] to-[#f0f9f1] border-[#1A4D2E]/20">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Coût estimé</p>
                    <p className="text-xl font-bold text-slate-700">{formatCurrency(totals.cost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Aides totales</p>
                    <p className="text-xl font-bold text-[#1A4D2E]">{formatCurrency(totals.aids)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Reste à charge</p>
                    <p className="text-xl font-bold text-[#E07A5F]">{formatCurrency(totals.resteACharge)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Taux de financement</p>
                    <p className="text-xl font-bold text-[#1A4D2E]">{totals.tauxFinancement}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulaires par type de travaux */}
          {allWorkTypes.map(workType => {
            const productQuestions = questions[workType];
            const productPrices = prices[workType]?.prices;
            const result = results[workType];
            const Icon = PRODUCT_ICONS[workType] || PRODUCT_ICONS.default;
            const isAdditional = additionalWorks.includes(workType);

            return (
              <Card key={workType}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-[#1A4D2E]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {prices[workType]?.label || workType}
                        </CardTitle>
                        {prices[workType]?.category && (
                          <CardDescription className="capitalize">
                            {prices[workType].category}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                    {isAdditional && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeWork(workType)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {productQuestions?.questions ? (
                    <ProductQuestionForm
                      workType={workType}
                      questions={productQuestions.questions}
                      answers={answers[workType] || {}}
                      onAnswerChange={(qId, val) => handleAnswerChange(workType, qId, val)}
                      prices={productPrices}
                    />
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      Questions en cours de chargement...
                    </p>
                  )}

                  {/* Bouton calculer */}
                  <Button 
                    onClick={() => calculateForWork(workType)}
                    disabled={loading}
                    className="w-full bg-[#1A4D2E] hover:bg-[#143d24]"
                  >
                    {loading ? "Calcul en cours..." : "Calculer l'estimation"}
                  </Button>

                  {/* Résultat */}
                  {result && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Coût estimé</span>
                        <span className="text-lg font-bold">{formatCurrency(result.estimated_cost)}</span>
                      </div>
                      
                      <div className="text-xs text-slate-500">
                        {Object.entries(result.cost_breakdown || {}).map(([key, val]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key.replace(/_/g, ' ')}</span>
                            <span>{val}</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-3 space-y-1">
                        {Object.entries(result.aids_estimated || {}).map(([aid, amount]) => (
                          <div key={aid} className="flex justify-between text-sm">
                            <span className="text-slate-600">{aid}</span>
                            <span className="font-medium text-[#1A4D2E]">-{formatCurrency(amount)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-3 flex justify-between items-center">
                        <span className="font-bold">Reste à charge</span>
                        <span className="text-xl font-bold text-[#E07A5F]">
                          {formatCurrency(result.reste_a_charge)}
                        </span>
                      </div>

                      {result.notes && result.notes.length > 0 && (
                        <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          {result.notes.map((note, i) => (
                            <p key={i}>{note}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Bouton ajouter un travail */}
          <Dialog open={showAddWorkModal} onOpenChange={setShowAddWorkModal}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full border-dashed border-2">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter d'autres travaux
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Ajouter des travaux</DialogTitle>
              </DialogHeader>
              <div className="grid gap-2">
                {availableProducts
                  .filter(p => !workTypes.includes(p.id) && !additionalWorks.includes(p.id))
                  .map(product => {
                    const Icon = PRODUCT_ICONS[product.id] || PRODUCT_ICONS.default;
                    return (
                      <button
                        key={product.id}
                        onClick={() => addWork(product.id)}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 text-left transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                          <Icon className="h-4 w-4 text-[#1A4D2E]" />
                        </div>
                        <div>
                          <p className="font-medium">{product.label}</p>
                          <p className="text-xs text-slate-500 capitalize">{product.category}</p>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </DialogContent>
          </Dialog>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={calculateAll}
              disabled={loading}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143d24]"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Calculer tout
            </Button>
            <Button 
              onClick={saveAudit}
              disabled={saving || Object.keys(results).length === 0}
              variant="outline"
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
          </div>

          {/* Avertissement */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Estimation indicative</p>
                <p>
                  Les montants affichés sont des estimations basées sur les prix moyens du marché 
                  et les barèmes d'aides en vigueur. Le montant définitif sera confirmé après 
                  obtention de devis et instruction des dossiers.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
